/**
 * Canny
 *
 * Gaussian blur, Sobel gradients, non-maximum suppression, hysteresis with
 * thresholds chosen from the gradient histogram rather than fixed constants —
 * palm photos vary far too much in lighting for fixed thresholds.
 *
 * In this pipeline Canny is run on the *vesselness* map (a valley-enhanced
 * image), not on raw luminance: see `ridge-filter.js` for why.
 *
 * © Manish Jagdish Thatte
 */

import { gaussianBlur, makeBinary, makeGray } from './image.js';

/** Sobel gradient → `{ mag, dir }` where `dir` is the quantised direction 0..3 (0°, 45°, 90°, 135°). */
export function sobel(gray) {
  const { width: w, height: h, data } = gray;
  const mag = makeGray(w, h); const dir = new Uint8Array(w * h);
  const at = (x, y) => data[Math.min(h - 1, Math.max(0, y)) * w + Math.min(w - 1, Math.max(0, x))];
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const gx = (at(x + 1, y - 1) + 2 * at(x + 1, y) + at(x + 1, y + 1)) - (at(x - 1, y - 1) + 2 * at(x - 1, y) + at(x - 1, y + 1));
    const gy = (at(x - 1, y + 1) + 2 * at(x, y + 1) + at(x + 1, y + 1)) - (at(x - 1, y - 1) + 2 * at(x, y - 1) + at(x + 1, y - 1));
    const i = y * w + x;
    mag.data[i] = Math.hypot(gx, gy);
    let a = Math.atan2(gy, gx) * 180 / Math.PI; if (a < 0) a += 180;
    dir[i] = a < 22.5 || a >= 157.5 ? 0 : a < 67.5 ? 1 : a < 112.5 ? 2 : 3;
  }
  return { mag, dir };
}

/** Keep only pixels that are a local maximum along the gradient direction. */
export function nonMaxSuppress(mag, dir) {
  const { width: w, height: h, data } = mag;
  const out = makeGray(w, h);
  const off = [[1, 0], [1, 1], [0, 1], [-1, 1]];   // gradient direction → neighbour offset
  for (let y = 1; y < h - 1; y++) for (let x = 1; x < w - 1; x++) {
    const i = y * w + x; const m = data[i]; if (m === 0) continue;
    const [dx, dy] = off[dir[i]];
    const a = data[(y + dy) * w + x + dx]; const b = data[(y - dy) * w + x - dx];
    // strict on one side, non-strict on the other: a plateau keeps exactly one pixel
    if (m > a && m >= b) out.data[i] = m;
  }
  return out;
}

/** Otsu's threshold on a histogram; returns the bin index that best separates the two classes. */
export function otsu(histogram) {
  let total = 0; let sumAll = 0;
  for (let i = 0; i < histogram.length; i++) { total += histogram[i]; sumAll += i * histogram[i]; }
  let wB = 0; let sumB = 0; let best = 0; let bestVar = -1;
  for (let t = 0; t < histogram.length; t++) {
    wB += histogram[t]; if (!wB) continue;
    const wF = total - wB; if (!wF) break;
    sumB += t * histogram[t];
    const mB = sumB / wB; const mF = (sumAll - sumB) / wF;
    const v = wB * wF * (mB - mF) ** 2;
    if (v > bestVar) { bestVar = v; best = t; }
  }
  return best;
}

/** Histogram of the non-zero values of `data`, in `bins` bins over [0, max]. */
export function histogramOf(data, bins = 256, ignoreZero = true) {
  let max = 0; for (let i = 0; i < data.length; i++) if (data[i] > max) max = data[i];
  const hist = new Float64Array(bins);
  if (max <= 0) return { hist, max: 0 };
  for (let i = 0; i < data.length; i++) {
    if (ignoreZero && data[i] <= 0) continue;
    hist[Math.min(bins - 1, Math.floor(data[i] / max * (bins - 1)))]++;
  }
  return { hist, max };
}

/**
 * Hysteresis: keep every `high` pixel, and every `low` pixel connected (8-way)
 * to one. Returns a binary image.
 */
export function hysteresis(suppressed, low, high) {
  const { width: w, height: h, data } = suppressed;
  const out = makeBinary(w, h); const stack = [];
  for (let i = 0; i < data.length; i++) if (data[i] >= high) { out.data[i] = 1; stack.push(i); }
  while (stack.length) {
    const p = stack.pop(); const px = p % w; const py = (p - px) / w;
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
      const xx = px + dx; const yy = py + dy;
      if (xx < 0 || yy < 0 || xx >= w || yy >= h) continue;
      const q = yy * w + xx;
      if (!out.data[q] && data[q] >= low && data[q] > 0) { out.data[q] = 1; stack.push(q); }
    }
  }
  return out;
}

/**
 * Canny with histogram-derived thresholds.
 *
 * `high` is Otsu's threshold over the non-zero suppressed gradient magnitudes;
 * `low` is `lowRatio × high`. A uniform image has no gradient at all, so nothing
 * is returned — not a border artefact.
 *
 * Returns `{ edges, thresholds: { low, high }, mag }`.
 */
export function canny(gray, { sigma = 1, lowRatio = 0.4, minMagnitude = 1e-4 } = {}) {
  const blurred = sigma > 0 ? gaussianBlur(gray, sigma) : gray;
  const { mag, dir } = sobel(blurred);
  const sup = nonMaxSuppress(mag, dir);
  let peak = 0; for (let i = 0; i < sup.data.length; i++) if (sup.data[i] > peak) peak = sup.data[i];
  if (peak < minMagnitude) {
    return { edges: makeBinary(gray.width, gray.height), thresholds: { low: 0, high: 0 }, mag };
  }
  const { hist, max } = histogramOf(sup.data);
  const high = (otsu(hist) + 1) / hist.length * max;
  const low = lowRatio * high;
  return { edges: hysteresis(sup, low, high), thresholds: { low, high }, mag };
}

/**
 * Hysteresis threshold of a *ridge* map (vesselness) with Otsu-derived levels.
 *
 * A valley filter already peaks on a line's centre, so there is nothing to
 * suppress across it; running Canny over the response would trace both flanks
 * of every rekhā and yield a double line. The gradient detector above remains
 * for what really is a step — the hand's outline.
 */
export function ridgeThreshold(map, { lowRatio = 0.5, floor = 0.02 } = {}) {
  const { hist, max } = histogramOf(map.data);
  if (max < floor) return { edges: makeBinary(map.width, map.height), thresholds: { low: 0, high: 0 } };
  const high = Math.max(floor, (otsu(hist) + 1) / hist.length * max);
  const low = lowRatio * high;
  return { edges: hysteresis(map, low, high), thresholds: { low, high } };
}
